import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardHeader, CardContent } from "@/components/ui/card";

const RANGES = [
  { label: "7D", value: "7d" },
  { label: "30D", value: "30d" },
  { label: "90D", value: "90d" },
  { label: "YTD", value: "ytd" },
  { label: "ALL", value: "all" },
] as const;

type RangeValue = (typeof RANGES)[number]["value"];

interface TrendPoint {
  date: string;
  count: number;
}

interface NamedCount {
  label: string;
  count: number;
  pct: number;
}

interface Analytics {
  range: RangeValue;
  rangeStart: string | null;
  kpis: {
    totalPassports: number;
    mintedPassports: number;
    mintedPct: number;
    newPassports: number;
    prevPassports: number;
    registeredHolders: number;
    totalSubmissions: number;
    openSubmissions: number;
    newSubmissions: number;
    prevSubmissions: number;
    avgResolutionHours: number | null;
  };
  issuanceTrend: TrendPoint[];
  submissionsTrend: TrendPoint[];
  topBrands: NamedCount[];
  statusBreakdown: NamedCount[];
  departmentBreakdown: NamedCount[];
  submissionsByType: NamedCount[];
  submissionsByStatus: NamedCount[];
}

const STATUS_COLOR: Record<string, string> = {
  Verified: "var(--accent)",
  Pending: "var(--warning)",
  Failed: "var(--danger)",
};

const SUBMISSION_STATUS_COLOR: Record<string, string> = {
  New: "var(--accent)",
  "In progress": "var(--warning)",
  Closed: "var(--success)",
};

function nf(n: number) {
  return n.toLocaleString("en-US");
}

function delta(current: number, prev: number): { text: string; up: boolean } {
  const diff = current - prev;
  if (prev === 0) {
    return { text: diff > 0 ? `▲ ${nf(diff)} new` : "no change", up: diff > 0 };
  }
  const pct = Math.round((diff / prev) * 100);
  const arrow = diff >= 0 ? "▲" : "▼";
  return { text: `${arrow} ${Math.abs(pct)}% vs prior`, up: diff >= 0 };
}

function sparkPath(points: TrendPoint[]): string {
  if (points.length === 0) return "";
  const max = Math.max(1, ...points.map((p) => p.count));
  const step = points.length > 1 ? 64 / (points.length - 1) : 0;
  return points
    .map((p, i) => {
      const x = (i * step).toFixed(1);
      const y = (26 - (p.count / max) * 24).toFixed(1);
      return `${i === 0 ? "M" : "L"}${x},${y}`;
    })
    .join(" ");
}

function fmtTickDate(iso: string) {
  const [, m, d] = iso.split("-");
  const months = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];
  return `${months[Number(m) - 1]} ${d}`;
}

function BarRow({
  label,
  count,
  pct,
  maxPct,
  color,
  showPct,
}: {
  label: string;
  count: number;
  pct: number;
  maxPct: number;
  color: string;
  showPct?: boolean;
}) {
  const width = maxPct > 0 ? Math.min(100, (pct / maxPct) * 100) : 0;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(40px, 90px) 52px",
        gap: 12,
        padding: "12px 22px",
        borderBottom: "1px solid var(--line)",
        alignItems: "center",
        fontSize: 13,
      }}
    >
      <span
        style={{
          color: "var(--ink)",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={label}
      >
        {label}
      </span>
      <div
        style={{ height: 6, background: "var(--surface-2)", position: "relative" }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: color,
            width: `${width}%`,
          }}
        />
      </div>
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 11,
          textAlign: "right",
          color: "var(--ink-soft)",
          letterSpacing: "0.06em",
        }}
      >
        {showPct ? `${pct}%` : nf(count)}
      </span>
    </div>
  );
}

function IssuanceChart({ points }: { points: TrendPoint[] }) {
  if (points.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--ink-mute)" }}>
        No issuance data in range
      </div>
    );
  }

  const W = 800;
  const H = 260;
  const padL = 40;
  const padR = 20;
  const padT = 20;
  const padB = 40;
  const max = Math.max(1, ...points.map((p) => p.count));
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const step = points.length > 1 ? plotW / (points.length - 1) : 0;

  const xy = points.map((p, i) => {
    const x = padL + i * step;
    const y = padT + plotH - (p.count / max) * plotH;
    return [x, y] as const;
  });

  const line = xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `M${xy[0][0].toFixed(1)},${(H - padB).toFixed(1)} L${line
    .split(" ")
    .join(" L")} L${xy[xy.length - 1][0].toFixed(1)},${(H - padB).toFixed(1)} Z`;

  const peak = points.reduce((a, b) => (b.count > a.count ? b : a), points[0]);
  const gridLines = [0.25, 0.5, 0.75, 1].map(
    (f) => padT + plotH - f * plotH,
  );
  const tickIdx = [
    0,
    Math.floor(points.length / 3),
    Math.floor((2 * points.length) / 3),
    points.length - 1,
  ].filter((v, i, a) => a.indexOf(v) === i);

  return (
    <div style={{ padding: 24 }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height: 260, display: "block" }}
      >
        <defs>
          <linearGradient id="g1" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.4" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <g stroke="var(--line)" strokeWidth="0.5">
          {gridLines.map((y) => (
            <line key={y} x1={padL} y1={y} x2={W - padR} y2={y} />
          ))}
        </g>
        <g
          fill="var(--ink-mute)"
          fontFamily="REM"
          fontSize="9"
          letterSpacing="0.1em"
        >
          {[1, 0.75, 0.5, 0.25].map((f, i) => (
            <text key={f} x="4" y={gridLines[3 - i] + 3}>
              {Math.round(max * f)}
            </text>
          ))}
        </g>
        <path d={area} fill="url(#g1)" />
        <polyline
          fill="none"
          stroke="var(--accent)"
          strokeWidth="1.8"
          points={line}
        />
        {points.length <= 40 && (
          <g fill="var(--accent)">
            {xy.map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r="2" />
            ))}
          </g>
        )}
        <g
          fill="var(--ink-mute)"
          fontFamily="REM"
          fontSize="9"
          letterSpacing="0.1em"
        >
          {tickIdx.map((idx) => (
            <text key={idx} x={padL + idx * step} y={H - 8}>
              {fmtTickDate(points[idx].date)}
            </text>
          ))}
        </g>
      </svg>
      <div
        style={{
          display: "flex",
          gap: 16,
          fontFamily: "var(--f-mono)",
          fontSize: 10.5,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "var(--ink-mute)",
          marginTop: 16,
        }}
      >
        <span>
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              background: "var(--accent)",
              marginRight: 6,
              verticalAlign: "middle",
            }}
          />
          Passports issued
        </span>
        <span style={{ marginLeft: "auto" }}>
          Peak: {fmtTickDate(peak.date)} · {peak.count} issued
        </span>
      </div>
    </div>
  );
}

function StatusDonut({
  total,
  segments,
}: {
  total: number;
  segments: NamedCount[];
}) {
  let cumulative = 0;
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 28, padding: 24 }}
    >
      <svg style={{ width: 140, height: 140, flexShrink: 0 }} viewBox="0 0 36 36">
        <circle
          cx="18"
          cy="18"
          r="15.9155"
          fill="none"
          stroke="var(--surface-3)"
          strokeWidth="3"
        />
        {segments.map((seg) => {
          const offset = -cumulative;
          cumulative += seg.pct;
          return (
            <circle
              key={seg.label}
              cx="18"
              cy="18"
              r="15.9155"
              fill="none"
              stroke={STATUS_COLOR[seg.label] ?? "var(--ink-mute)"}
              strokeWidth="3"
              strokeDasharray={`${seg.pct} ${100 - seg.pct}`}
              strokeDashoffset={offset}
              transform="rotate(-90 18 18)"
              strokeLinecap="butt"
            />
          );
        })}
        <text
          x="18"
          y="17"
          textAnchor="middle"
          fontFamily="REM"
          fontSize="6"
          fontWeight="500"
          fill="var(--ink)"
        >
          {nf(total)}
        </text>
        <text
          x="18"
          y="23"
          textAnchor="middle"
          fontFamily="REM"
          fontSize="2.5"
          letterSpacing="0.2em"
          fill="var(--ink-mute)"
        >
          TOTAL
        </text>
      </svg>
      <div
        style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}
      >
        {segments.map((seg) => (
          <div
            key={seg.label}
            style={{
              display: "grid",
              gridTemplateColumns: "14px 1fr auto",
              gap: 12,
              fontSize: 13,
              alignItems: "center",
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                background: STATUS_COLOR[seg.label] ?? "var(--ink-mute)",
                display: "inline-block",
              }}
            />
            <span>{seg.label}</span>
            <span
              style={{
                fontFamily: "var(--f-mono)",
                fontSize: 11,
                letterSpacing: "0.06em",
                color: "var(--ink-soft)",
              }}
            >
              {seg.pct}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AnalyticsPage() {
  const [range, setRange] = useState<RangeValue>("30d");
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get<Analytics>("/admin/analytics", { params: { range } })
      .then(({ data: d }) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Failed to load analytics");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  const k = data?.kpis;
  const passportDelta = k ? delta(k.newPassports, k.prevPassports) : null;
  const submissionDelta = k ? delta(k.newSubmissions, k.prevSubmissions) : null;

  const kpis = k
    ? [
        {
          label: "Total passports",
          value: nf(k.totalPassports),
          delta: passportDelta?.text ?? "",
          up: passportDelta?.up ?? false,
          spark: data ? sparkPath(data.issuanceTrend) : "",
          sparkColor: "var(--accent)",
        },
        {
          label: "Verified · minted",
          value: nf(k.mintedPassports),
          delta: `${k.mintedPct}% of total`,
          up: true,
          spark: "",
          sparkColor: "var(--success)",
        },
        {
          label: "Registered holders",
          value: nf(k.registeredHolders),
          delta: "wallet accounts",
          up: false,
          spark: "",
          sparkColor: "var(--ink-soft)",
        },
        {
          label: "Submissions",
          value: nf(k.totalSubmissions),
          delta: `${nf(k.openSubmissions)} open · ${submissionDelta?.text ?? ""}`,
          up: submissionDelta?.up ?? false,
          spark: data ? sparkPath(data.submissionsTrend) : "",
          sparkColor: "var(--ink-soft)",
        },
      ]
    : [];

  const maxBrandPct = data
    ? Math.max(1, ...data.topBrands.map((b) => b.pct))
    : 1;
  const maxTypePct = data
    ? Math.max(1, ...data.submissionsByType.map((t) => t.pct))
    : 1;
  const maxDeptPct = data
    ? Math.max(1, ...data.departmentBreakdown.map((d) => d.pct))
    : 1;

  return (
    <>
      <div className="adm__body">
        <div className="adm__page-head">
          <div className="adm__title-stack">
            <div className="meta">↗ ANALYTICS · DPP · INTAKE</div>
            <h1 className="h1" style={{ fontSize: 36, margin: 0 }}>
              Analytics
            </h1>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 12,
            marginBottom: 24,
          }}
        >
          <span className="meta">RANGE</span>
          <div className="seg">
            {RANGES.map((r) => (
              <button
                key={r.value}
                className={range === r.value ? "is-active" : ""}
                onClick={() => setRange(r.value)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div
            style={{
              padding: 16,
              border: "1px solid var(--danger)",
              color: "var(--danger)",
              marginBottom: 24,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {loading && !data && (
          <div style={{ padding: 40, color: "var(--ink-mute)" }}>Loading…</div>
        )}

        {data && k && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                border: "1px solid var(--line)",
                marginBottom: 24,
                opacity: loading ? 0.6 : 1,
              }}
            >
              {kpis.map((kpi, i) => (
                <div
                  key={kpi.label}
                  style={{
                    padding: 22,
                    borderRight: i < 3 ? "1px solid var(--line)" : undefined,
                    background: "var(--surface)",
                    position: "relative",
                    minHeight: 130,
                  }}
                >
                  <div
                    style={{
                      fontFamily: "var(--f-mono)",
                      fontSize: 10,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: "var(--ink-mute)",
                    }}
                  >
                    {kpi.label}
                  </div>
                  {kpi.spark && (
                    <svg
                      style={{
                        position: "absolute",
                        right: 18,
                        top: 18,
                        width: 64,
                        height: 28,
                      }}
                      viewBox="0 0 64 28"
                    >
                      <polyline
                        fill="none"
                        stroke={kpi.sparkColor}
                        strokeWidth="1.2"
                        points={kpi.spark.replace(/[ML]/g, " ").trim()}
                      />
                    </svg>
                  )}
                  <div
                    style={{
                      fontFamily: "var(--f-head)",
                      fontWeight: 500,
                      fontSize: 32,
                      lineHeight: 1,
                      marginTop: 14,
                    }}
                  >
                    {kpi.value}
                  </div>
                  <div
                    style={{
                      marginTop: 10,
                      fontFamily: "var(--f-mono)",
                      fontSize: 11,
                      letterSpacing: "0.08em",
                      color: kpi.up ? "var(--success)" : "var(--ink-mute)",
                    }}
                  >
                    {kpi.delta}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.4fr 1fr",
                gap: 24,
                marginBottom: 24,
              }}
            >
              <Card style={{ margin: 0 }}>
                <CardHeader>
                  <h3>Issuance trend</h3>
                  <span className="meta">PASSPORTS CREATED PER DAY</span>
                </CardHeader>
                <IssuanceChart points={data.issuanceTrend} />
              </Card>

              <Card style={{ margin: 0 }}>
                <CardHeader>
                  <h3>Top brands</h3>
                  <span className="meta">{nf(k.totalPassports)} PASSPORTS</span>
                </CardHeader>
                <div>
                  {data.topBrands.length === 0 && (
                    <div
                      style={{ padding: 22, color: "var(--ink-mute)", fontSize: 13 }}
                    >
                      No passports yet
                    </div>
                  )}
                  {data.topBrands.map((b) => (
                    <BarRow
                      key={b.label}
                      label={b.label}
                      count={b.count}
                      pct={b.pct}
                      maxPct={maxBrandPct}
                      color={
                        b.label.startsWith("Other")
                          ? "var(--ink-soft)"
                          : "var(--accent)"
                      }
                      showPct
                    />
                  ))}
                </div>
              </Card>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 24,
                marginBottom: 24,
              }}
            >
              <Card style={{ margin: 0 }}>
                <CardHeader>
                  <h3>Passport status</h3>
                  <span className="meta">CURRENT</span>
                </CardHeader>
                <StatusDonut
                  total={k.totalPassports}
                  segments={data.statusBreakdown}
                />
              </Card>

              <Card style={{ margin: 0 }}>
                <CardHeader>
                  <h3>By department</h3>
                  <span className="meta">{nf(k.totalPassports)} PASSPORTS</span>
                </CardHeader>
                <div>
                  {data.departmentBreakdown.map((d) => (
                    <BarRow
                      key={d.label}
                      label={d.label}
                      count={d.count}
                      pct={d.pct}
                      maxPct={maxDeptPct}
                      color="var(--accent)"
                      showPct
                    />
                  ))}
                </div>
              </Card>

              <Card style={{ margin: 0 }}>
                <CardHeader>
                  <h3>Submissions by form</h3>
                  <span className="meta">{nf(k.totalSubmissions)} TOTAL</span>
                </CardHeader>
                <div>
                  {data.submissionsByType.length === 0 && (
                    <div
                      style={{ padding: 22, color: "var(--ink-mute)", fontSize: 13 }}
                    >
                      No submissions yet
                    </div>
                  )}
                  {data.submissionsByType.map((t) => (
                    <BarRow
                      key={t.label}
                      label={t.label}
                      count={t.count}
                      pct={t.pct}
                      maxPct={maxTypePct}
                      color="var(--accent)"
                    />
                  ))}
                </div>
              </Card>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 24,
              }}
            >
              <Card style={{ margin: 0 }}>
                <CardHeader>
                  <h3>Submission pipeline</h3>
                  <span className="meta">BY STATUS</span>
                </CardHeader>
                <CardContent>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 14,
                    }}
                  >
                    {data.submissionsByStatus.map((s) => (
                      <div key={s.label}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 13,
                            marginBottom: 6,
                          }}
                        >
                          <span>{s.label}</span>
                          <span
                            style={{
                              fontFamily: "var(--f-mono)",
                              fontSize: 11,
                              color: "var(--ink-soft)",
                            }}
                          >
                            {nf(s.count)} · {s.pct}%
                          </span>
                        </div>
                        <div
                          style={{
                            height: 6,
                            background: "var(--surface-2)",
                            position: "relative",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              width: `${s.pct}%`,
                              background:
                                SUBMISSION_STATUS_COLOR[s.label] ??
                                "var(--ink-mute)",
                            }}
                          />
                        </div>
                      </div>
                    ))}
                    {data.submissionsByStatus.length === 0 && (
                      <span style={{ color: "var(--ink-mute)", fontSize: 13 }}>
                        No submissions yet
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card style={{ margin: 0 }}>
                <CardHeader>
                  <h3>Resolution time</h3>
                  <span className="meta">CLOSED SUBMISSIONS</span>
                </CardHeader>
                <CardContent>
                  <div className="row between mt-16">
                    <div>
                      <div className="meta text-mute">AVG TIME TO CLOSE</div>
                      <div
                        style={{
                          fontFamily: "var(--f-head)",
                          fontWeight: 500,
                          fontSize: 28,
                          color: "var(--accent)",
                        }}
                      >
                        {k.avgResolutionHours != null
                          ? `${k.avgResolutionHours}h`
                          : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="meta text-mute">OPEN</div>
                      <div
                        style={{
                          fontFamily: "var(--f-head)",
                          fontWeight: 500,
                          fontSize: 28,
                        }}
                      >
                        {nf(k.openSubmissions)}
                      </div>
                    </div>
                    <div>
                      <div className="meta text-mute">TOTAL</div>
                      <div
                        style={{
                          fontFamily: "var(--f-head)",
                          fontWeight: 500,
                          fontSize: 28,
                        }}
                      >
                        {nf(k.totalSubmissions)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </>
  );
}
